import {localizeName, t} from "./i18n";

export const COMBO_CATEGORY_ID = 142;
export const DRINK_CATEGORY_ID = 93;
export const SOUP_CATEGORY_ID = 172;
//套餐可選的加購品類別（飲品、湯品），同後端 ComboAddon::ADDON_CATEGORY_IDS；陣列順序＝畫面分組順序
export const COMBO_ADDON_CATEGORY_IDS = [DRINK_CATEGORY_ID, SOUP_CATEGORY_ID];
//每份套餐飲品／湯品最多折抵金額（不超過該品項單價，湯品 40 元只折 40）
export const COMBO_ADDON_DISCOUNT = 50;

export const isComboAddonCategory = (categoryId) =>
  COMBO_ADDON_CATEGORY_IDS.some((id) => String(id) === String(categoryId));

//自助點餐不販售的類別：16兌換專區 104優惠 123活動專區 124點數兌換
export const HIDDEN_CATEGORY_IDS = new Set(["16", "104", "123", "124"]);

export const sameId = (left, right) => String(left) === String(right);

//status: 1=正常 2=暫售 3+=停售；價格<0 的品項比照線上點餐不顯示
export const isVisibleMeal = (meal) =>
  meal.kiosk_visible !== false &&
  Number(meal.kiosk_visible) !== 0 &&
  Number(meal.price ?? 0) >= 0 &&
  Number(meal.status ?? 1) < 3 &&
  !HIDDEN_CATEGORY_IDS.has(String(meal.category_id));

export const isVisibleOption = (food) =>
  Number(food.price ?? 0) >= 0 &&
  Number(food.status ?? 1) < 2 &&
  !HIDDEN_CATEGORY_IDS.has(String(food.category_id));

export const createSessionId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `kiosk-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getMeal = (systemData, mealId) =>
  (systemData?.meal ?? []).find((meal) => sameId(meal.id, mealId));

export const getFood = (systemData, foodId) =>
  (systemData?.food ?? []).find((food) => sameId(food.pd_id, foodId));

export const getCategory = (systemData, categoryId) =>
  (systemData?.menu ?? []).find((menu) => sameId(menu.category_id, categoryId));

export const getVisibleCategories = (systemData) => {
  const mealCategoryIds = new Set(
    (systemData?.meal ?? []).filter(isVisibleMeal).map((meal) => String(meal.category_id))
  );
  return (systemData?.menu ?? [])
    .filter((menu) => !HIDDEN_CATEGORY_IDS.has(String(menu.category_id)))
    .filter((menu) => Number(menu.status ?? 1) === 1)
    .filter((menu) => mealCategoryIds.has(String(menu.category_id)))
    .sort((a, b) => Number(a.kiosk_sort ?? a.sort ?? 0) - Number(b.kiosk_sort ?? b.sort ?? 0));
};

export const getVisibleMeals = (systemData, categoryId) =>
  (systemData?.meal ?? [])
    .filter((meal) => sameId(meal.category_id, categoryId) && isVisibleMeal(meal))
    .sort(
      (a, b) =>
        Number(a.status ?? 0) - Number(b.status ?? 0) ||
        Number(a.kiosk_sort ?? a.sort ?? 0) - Number(b.kiosk_sort ?? b.sort ?? 0)
    );

const comboAddonOrder = (categoryId) =>
  COMBO_ADDON_CATEGORY_IDS.findIndex((id) => String(id) === String(categoryId));

//套餐可選的飲品與湯品（飲品在前，各自依 sort）
export const getComboDrinks = (systemData) =>
  (systemData?.meal ?? [])
    .filter((meal) => isComboAddonCategory(meal.category_id) && isVisibleMeal(meal) && Number(meal.status ?? 1) < 2)
    .sort(
      (a, b) =>
        comboAddonOrder(a.category_id) - comboAddonOrder(b.category_id) ||
        Number(a.sort ?? 0) - Number(b.sort ?? 0)
    );

//細項沒賣（暫售/停售/負價）不顯示；整個規格類別都沒得選就整組隱藏，必選驗證也一併跳過
export const getCompositionSections = (systemData, meal) =>
  (meal?.composition ?? [])
    .map((composition, index) => ({
      ...composition,
      index,
      menu: getCategory(systemData, composition.category_id),
      options: (systemData?.food ?? [])
        .filter((food) => sameId(food.category_id, composition.category_id) && isVisibleOption(food))
        .sort((a, b) => Number(a.sort ?? 0) - Number(b.sort ?? 0)),
    }))
    .filter((section) => section.options.length > 0)
    .sort((a, b) => Number(b.menu?.required === 1) - Number(a.menu?.required === 1) || Number(a.sort ?? a.index) - Number(b.sort ?? b.index));

//細項連動類別（food.bind_category_ids，後端 t_product_detail_bind）：
//已選的綁定類別細項（如「套餐A」）觸發額外規格組（如「醬料」）；只算一層，已在 composition 的類別不重複
export const getLinkedSections = (systemData, meal, detail) => {
  const boundIds = new Set((meal?.composition ?? []).map((composition) => String(composition.category_id)));
  const linkedIds = [];
  (detail ?? []).forEach((selected) => {
    const food = getFood(systemData, selected.food_id);
    if (!food || !boundIds.has(String(food.category_id))) return;
    (food.bind_category_ids ?? []).forEach((categoryId) => {
      const key = String(categoryId);
      if (!boundIds.has(key) && !linkedIds.includes(key)) linkedIds.push(key);
    });
  });
  return linkedIds
    .map((categoryId) => ({
      category_id: Number(categoryId),
      linked: true,
      menu: getCategory(systemData, categoryId),
      options: (systemData?.food ?? [])
        .filter((food) => sameId(food.category_id, categoryId) && isVisibleOption(food))
        .sort((a, b) => Number(a.sort ?? 0) - Number(b.sort ?? 0)),
    }))
    .filter((section) => section.options.length > 0);
};

//取消觸發細項後清掉殘留的連動選擇，避免後端以「不屬於此商品」拒絕
export const pruneStaleLinkedDetail = (systemData, meal, detail) => {
  const boundIds = new Set((meal?.composition ?? []).map((composition) => String(composition.category_id)));
  const linkTargets = new Set();
  (systemData?.food ?? []).forEach((food) => {
    (food.bind_category_ids ?? []).forEach((categoryId) => linkTargets.add(String(categoryId)));
  });
  const activeIds = new Set(getLinkedSections(systemData, meal, detail).map((section) => String(section.category_id)));
  return (detail ?? []).filter((selected) => {
    const categoryId = String(getFood(systemData, selected.food_id)?.category_id);
    return boundIds.has(categoryId) || !linkTargets.has(categoryId) || activeIds.has(categoryId);
  });
};

export const updateOptionSelection = (systemData, detail, option, operation = "toggle") => {
  const current = detail ?? [];
  const menu = getCategory(systemData, option.category_id);
  const type = menu?.type ?? "checkbox";
  const existing = current.find((item) => sameId(item.food_id, option.pd_id));

  if (operation === "remove") {
    if (!existing) return current;
    if (type === "selected" && Number(existing.qty) > 1) {
      return current.map((item) =>
        sameId(item.food_id, option.pd_id) ? {...item, qty: Number(item.qty) - 1} : item
      );
    }
    return current.filter((item) => !sameId(item.food_id, option.pd_id));
  }

  if (type === "radio") {
    return [
      ...current.filter((item) => {
        const food = getFood(systemData, item.food_id);
        return !sameId(food?.category_id, option.category_id);
      }),
      {food_id: option.pd_id, qty: 1},
    ];
  }

  if (type === "selected") {
    if (existing) {
      const max = Number(menu?.max_select ?? option.max_select ?? 99);
      return current.map((item) =>
        sameId(item.food_id, option.pd_id)
          ? {...item, qty: Math.min(max, Number(item.qty) + 1)}
          : item
      );
    }
    return [...current, {food_id: option.pd_id, qty: 1}];
  }

  return existing
    ? current.filter((item) => !sameId(item.food_id, option.pd_id))
    : [...current, {food_id: option.pd_id, qty: 1}];
};

export const validateCustomization = (systemData, meal, detail) => {
  const missing = getCompositionSections(systemData, meal).filter((section) => {
    if (Number(section.menu?.required) !== 1 && Number(section.min_select ?? section.menu?.min_select ?? 0) < 1) {
      return false;
    }
    return !(detail ?? []).some((item) => {
      const food = getFood(systemData, item.food_id);
      return sameId(food?.category_id, section.category_id);
    });
  });

  //連動類別的必選：只在觸發細項已被選時檢查（與後端 validateDetail 同口徑）
  const missingLinked = getLinkedSections(systemData, meal, detail).filter((section) => {
    const required = Number(section.menu?.required ?? 0);
    if (required < 1) return false;
    const selectedQty = (detail ?? []).reduce((sum, item) => {
      const food = getFood(systemData, item.food_id);
      return sameId(food?.category_id, section.category_id) ? sum + Number(item.qty ?? 1) : sum;
    }, 0);
    return selectedQty < required;
  });

  const allMissing = [...missing, ...missingLinked];
  return {valid: allMissing.length === 0, missing: allMissing};
};

//套餐飲品／湯品單價（含其客製選項），沒選回 0
export const comboDrinkUnitPrice = (systemData, item) => {
  const drink = item?.combo_drink;
  if (!drink?.meal_id) return 0;
  return (
    Number(getMeal(systemData, drink.meal_id)?.price ?? 0) +
    (drink.detail ?? []).reduce((sum, detail) => {
      const food = getFood(systemData, detail.food_id);
      return sum + Number(food?.price ?? 0) * Number(detail.qty ?? 1);
    }, 0)
  );
};

export const itemUnitPrice = (systemData, item) => {
  const meal = getMeal(systemData, item.meal_id);
  const detailPrice = (item.detail ?? []).reduce((sum, detail) => {
    const food = getFood(systemData, detail.food_id);
    return sum + Number(food?.price ?? 0) * Number(detail.qty ?? 1);
  }, 0);
  return Number(meal?.price ?? 0) + detailPrice + comboDrinkUnitPrice(systemData, item);
};

export const cartTotal = (systemData, cart) =>
  (cart ?? []).reduce(
    (sum, item) => sum + itemUnitPrice(systemData, item) * Number(item.qty ?? 1),
    0
  );

//套餐優惠規則鏡射後端（KioskController::autoDiscountPreview／POSServiceProvider::processOrder），改規則要同步改
//主餐分類（主餐＋套餐飲品每組折20）
export const MAIN_MEAL_CATEGORY_IDS = new Set(["102", "139", "111", "97"]);
//套餐加購品項（加購＋套餐飲品／湯品每份折抵至多50）：142 類別、有價格的細項，同後端 ComboAddon::comboPdIds()
export const isComboAddonFood = (systemData, foodId) => {
  const food = getFood(systemData, foodId);
  return Boolean(food) && sameId(food.category_id, COMBO_CATEGORY_ID) && Number(food.price ?? 0) > 0;
};
//飲料計數品項（processOrder 以 pd 565 計算飲料數）
export const DRINK_COUNT_FOOD_ID = 565;

//單一購物車品項可直接歸屬的套餐優惠（正數）。明細數量比照後端不乘主餐數量，
//所以同品項調高數量時折扣不會等比放大（與 doBuycart／processOrder 寫入方式一致）
export const itemComboDiscount = (systemData, item) => {
  if (!item?.combo_drink?.meal_id) return 0;
  const qty = Number(item.qty ?? 1);
  const addonQty = (item.detail ?? []).reduce(
    (sum, detail) =>
      isComboAddonFood(systemData, detail.food_id) ? sum + Number(detail.qty ?? 1) : sum,
    0
  );
  const unitDiscount = Math.max(0, Math.min(COMBO_ADDON_DISCOUNT, comboDrinkUnitPrice(systemData, item)));
  let discount = Math.min(addonQty, qty) * unitDiscount;
  const meal = getMeal(systemData, item.meal_id);
  if (MAIN_MEAL_CATEGORY_IDS.has(String(meal?.category_id))) {
    const drinkItemQty = (item.combo_drink.detail ?? []).reduce(
      (sum, detail) =>
        sameId(detail.food_id, DRINK_COUNT_FOOD_ID) ? sum + Number(detail.qty ?? 1) : sum,
      0
    );
    discount += Math.min(qty, drinkItemQty) * 20;
  }
  return discount;
};

//品項小計（原價×數量－套餐優惠），購物車與客製頁顯示用
export const itemLineTotal = (systemData, item) =>
  itemUnitPrice(systemData, item) * Number(item.qty ?? 1) - itemComboDiscount(systemData, item);

export const cartComboDiscount = (systemData, cart) =>
  (cart ?? []).reduce((sum, item) => sum + itemComboDiscount(systemData, item), 0);

export const describeItemOptions = (systemData, item, lang = "zh") => {
  const names = (item.detail ?? []).flatMap((detail) => {
    const food = getFood(systemData, detail.food_id);
    //「套餐超值加購」選項只是套餐標記，購物車顯示下方的「套餐飲品：Ｘ」即可，不重複列出
    if (sameId(food?.category_id, COMBO_CATEGORY_ID)) return [];
    return [`${localizeName(food, lang) || (food?.name ?? "未知規格")}${Number(detail.qty) > 1 ? ` ×${detail.qty}` : ""}`];
  });
  if (item.combo_drink?.meal_id) {
    const drink = getMeal(systemData, item.combo_drink.meal_id);
    names.push(`${t(lang, "comboDrink")}：${localizeName(drink, lang) || (drink?.name ?? "飲品")}`);
  }
  return names;
};

// 細項份量：後台有填單位才顯示，例如白飯 180／g → 「180g」；沒填單位回空字串
export const detailAmount = (food) => {
  const unit = String(food?.unit ?? "").trim();
  if (!unit) return "";
  const qty = Number(food?.qty);
  return `${Number.isFinite(qty) && qty > 0 ? qty : ""}${unit}`;
};
